import {
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Fab,
  Chip,
  useTheme,
  TextField,
  InputAdornment,
} from '@mui/material';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { EmptyState } from '@/shared/ui';
import { useState, useEffect, useRef } from 'react';
import { useAppDispatch } from '@/app';
import TocIcon from '@mui/icons-material/Toc';
import GridViewIcon from '@mui/icons-material/GridView';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderIcon from '@mui/icons-material/Folder';
import SearchIcon from '@mui/icons-material/Search';
import {
  setCreativeDetailsModal,
  setAddCreativeModal,
  setDeleteCreativeModal,
  setBrandCreativeModal,
} from '../creativesSlice';
import { CustomTable } from '@/shared/ui/Table';
import { useCreativesQuery } from '../hooks/useCreativesQueries';
import { useDebounce, useQueryErrorSnackbar } from '@/shared/hooks';
import AddIcon from '@mui/icons-material/Add';
import { VAST_THUMBNAIL, WEBVIEW_THUMBNAIL } from '../../campaigns/utils';
import {
  CREATIVE_TYPE,
  isUrlCreative,
  normalizeCreativeType,
} from '@/shared/constants/creativeTypes';
import {
  CREATIVE_DEFAULT_HEADERS,
  CREATIVE_LIBRARY_HEADERS,
} from '../constants';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

type ActionSpec = {
  label: 'view' | 'edit' | 'delete';
  disabled?: boolean;
};

interface ContentProps {
  creatives?: any;
  enableRowSelection?: boolean;
  selectedCreatives?: any[];
  setSelectedCreatives?: React.Dispatch<React.SetStateAction<any[]>>;
  paginationPosition?: 'fixed' | 'inline' | 'none';
  showButton?: boolean;
  tableView?: boolean;
  action?: ActionSpec[];
  mode?: 'default' | 'library';
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
}

const Creative = ({
  creatives,
  enableRowSelection = false,
  selectedCreatives = [],
  setSelectedCreatives = () => {},
  paginationPosition = 'fixed',
  showButton = true,
  tableView = true,
  action = [],
  mode = 'default',
  onPageChange,
  onRowsPerPageChange,
}: ContentProps) => {
  const theme = useTheme();
  const [showTableView, setShowTableView] = useState(
    mode === 'library' ? true : tableView
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 1000);
  const [currentMediaIndex, setCurrentMediaIndex] = useState<
    Record<string, number>
  >({});
  const autoAdvanceTimers = useRef<Record<string, NodeJS.Timeout>>({});
  const dispatch = useAppDispatch();

  const {
    data: fetchedContent,
    isLoading,
    error,
    isError,
  } = useCreativesQuery({ page, rowsPerPage, search: debouncedSearch }, {
    enabled: !creatives,
  } as any);

  useQueryErrorSnackbar(error, 'Failed to load creatives');

  // Use provided creatives or fetched content
  const content = creatives || fetchedContent;
  let columns = [
    ...((mode === 'library'
      ? CREATIVE_LIBRARY_HEADERS
      : creatives?.headers || CREATIVE_DEFAULT_HEADERS) as any[]),
  ];
  if (action.length > 0) {
    columns.push({
      id: 'actions',
      label: 'Actions',
      type: 'actions',
      sortable: false,
    });
  }

  const rows =
    content?.rows?.map((item: any) => {
      const formatDate = (dateValue: any) => {
        if (!dateValue || dateValue === '-') return '-';
        let date = dayjs(dateValue, 'DD-MM-YYYY HH:mm:ss', true);
        if (!date.isValid()) {
          date = dayjs.utc(dateValue);
        }
        return date.isValid()
          ? date.tz('Asia/Kolkata').format('D MMM, YYYY h:mm A')
          : '-';
      };

      const filesCount = Array.isArray(item.files) ? item.files.length : 0;

      const createdByUser = item?.createdByUser;
      const updatedByUser = item?.updatedByUser || item?.createdByUser;

      const formattedCreatedAt = formatDate(item.createdAt);
      const formattedUpdatedAt = formatDate(item.updatedAt);

      const createdByName = createdByUser?.name || createdByUser?.email || '-';
      const updatedByName = updatedByUser?.name || updatedByUser?.email || '-';

      const brandNameNode = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FolderIcon sx={{ color: 'warning.main', fontSize: 28 }} />
          <Typography variant="body2" fontWeight={600} noWrap>
            {item?.name || '-'}
          </Typography>
        </Box>
      );

      const createdAtNode = (
        <Box>
          <Typography variant="body2">{createdByName}</Typography>
          <Typography variant="caption">{formattedCreatedAt}</Typography>
        </Box>
      );

      const updatedAtNode = (
        <Box>
          <Typography variant="body2">{updatedByName}</Typography>
          <Typography variant="caption">{formattedUpdatedAt}</Typography>
        </Box>
      );

      return {
        ...item,
        ...(mode === 'library'
          ? {
              brandName: brandNameNode,
              creativeCount: filesCount,
              createdAtInfo: createdAtNode,
              updatedAtInfo: updatedAtNode,
            }
          : {
              files: Object.values(item.files || {}),
            }),
        createdAt: formattedCreatedAt,
        updatedAt: formattedUpdatedAt,
      };
    }) || [];

  const totalItems = content?.totalItems ?? content?.total;

  const showImagePreview = (creative: any) => {
    if (creative) {
      dispatch(
        setCreativeDetailsModal({ open: true, selectedCreative: creative })
      );
    }
  };

  const handleDeleteCreative = (creative: any) => {
    dispatch(
      setDeleteCreativeModal({ open: true, selectedCreative: creative })
    );
  };

  const handleViewCreative = (creative: any) => {
    if (mode === 'library') {
      dispatch(setBrandCreativeModal({ open: true, selectedBrand: creative }));
    } else {
      showImagePreview(creative);
    }
  };

  const getMediaFiles = (creative: any) => {
    const filesSource =
      creative.files && Object.keys(creative.files).length > 0
        ? creative.files
        : creative.src?.files || {};

    return Object.values(filesSource) as {
      src: string;
      type?: string;
      resolution?: any;
    }[];
  };

  // Helper function to get current media for a creative
  const getCurrentMedia = (creative: any) => {
    const mediaFiles = getMediaFiles(creative);
    const currentIndex = currentMediaIndex[creative.id] || 0;
    return mediaFiles[currentIndex] || null;
  };

  // Auto-advance to next media
  const autoAdvanceToNext = (creativeId: string) => {
    const creative = rows.find((r: any) => r.id === creativeId);
    if (!creative) {
      stopAutoAdvance(creativeId);
      return;
    }

    const mediaFiles = getMediaFiles(creative);
    if (mediaFiles.length <= 1) {
      stopAutoAdvance(creativeId);
      return;
    }

    setCurrentMediaIndex(prev => {
      const currentIndex = prev[creativeId] || 0;
      const nextIndex =
        currentIndex < mediaFiles.length - 1 ? currentIndex + 1 : 0;

      const newState = {
        ...prev,
        [creativeId]: nextIndex,
      };
      return newState;
    });
  };

  // Start auto-advance timer
  const startAutoAdvance = (creativeId: string) => {
    // Clear any existing timer
    if (autoAdvanceTimers.current[creativeId]) {
      clearInterval(autoAdvanceTimers.current[creativeId]);
    }

    // Start new timer (advance every 1.5 seconds for smoother experience)
    autoAdvanceTimers.current[creativeId] = setInterval(() => {
      autoAdvanceToNext(creativeId);
    }, 1500);
  };

  // Stop auto-advance timer
  const stopAutoAdvance = (creativeId: string) => {
    if (autoAdvanceTimers.current[creativeId]) {
      clearInterval(autoAdvanceTimers.current[creativeId]);
      delete autoAdvanceTimers.current[creativeId];
    }
  };

  // Handle mouse enter for auto-advance
  const handleMouseEnter = (creativeId: string) => {
    const creative = rows.find((r: any) => r.id === creativeId);
    if (!creative) return;

    const mediaFiles = getMediaFiles(creative);
    if (mediaFiles.length > 1) {
      // Always start fresh timer on mouse enter
      startAutoAdvance(creativeId);
    }
  };

  // Handle mouse leave to stop auto-advance
  const handleMouseLeave = (creativeId: string) => {
    stopAutoAdvance(creativeId);
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(autoAdvanceTimers.current).forEach(timer => {
        clearInterval(timer);
      });
    };
  }, []);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  // selectedCreatives now contains full row objects, so we can use them directly
  const selectedRows = selectedCreatives || [];

  // Handle selection changes from CustomTable
  const handleSelectionChange = (selectedRows: any[]) => {
    // Keep the full row objects, not just IDs
    setSelectedCreatives(selectedRows);
  };

  // Only show loading/error states when fetching data (not when using provided creatives)
  if (!creatives && isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!creatives && isError) {
    return (
      <Box p={2}>
        <Alert severity="error">
          {error instanceof Error ? error.message : 'Failed to load creatives'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      {mode === 'library' && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            px: 2,
            pt: 2,
            pb: 1,
          }}
        >
          <TextField
            size="small"
            placeholder="Search brand folder (min 3 characters)"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: '100%', maxWidth: 450 }}
          />
        </Box>
      )}
      {rows.length === 0 ? (
        <Box sx={{ position: 'relative' }}>
          <EmptyState
            title="No creatives uploaded"
            description="Upload your first creative to start creating engaging content for your campaigns."
            showButton={false}
          />
        </Box>
      ) : (
        <Box>
          {mode !== 'library' && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
                p: 2,
              }}
            >
              <Box
                sx={{
                  borderRadius: '1rem',
                  border: `1px solid ${theme.palette.divider}`,
                  width: 'fit-content',
                  display: 'flex',
                  overflow: 'hidden',
                  bgcolor: theme.palette.background.paper,
                }}
              >
                <IconButton
                  onClick={() => setShowTableView(true)}
                  sx={{
                    borderRadius: 0,
                    backgroundColor: showTableView
                      ? theme.palette.mode === 'dark'
                        ? theme.palette.primary.dark
                        : '#D6F0FC'
                      : 'transparent',
                    color: showTableView
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                    '&:hover': {
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? theme.palette.action.hover
                          : '#B5E4FA',
                    },
                  }}
                >
                  <TocIcon />
                </IconButton>

                <IconButton
                  onClick={() => setShowTableView(false)}
                  sx={{
                    borderRadius: 0,
                    backgroundColor: !showTableView
                      ? theme.palette.mode === 'dark'
                        ? theme.palette.primary.dark
                        : '#D6F0FC'
                      : 'transparent',
                    color: !showTableView
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                    '&:hover': {
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? theme.palette.action.hover
                          : '#B5E4FA',
                    },
                  }}
                >
                  <GridViewIcon />
                </IconButton>
              </Box>
            </Box>
          )}

          {showTableView ? (
            <Box
              sx={{
                margin: '0rem 1rem',
              }}
            >
              <CustomTable
                columns={columns}
                rows={rows}
                page={creatives ? (creatives.page ?? page) : page}
                setPage={p => {
                  setPage(p);
                  onPageChange?.(p);
                }}
                rowsPerPage={
                  creatives
                    ? (creatives.rowsPerPage ?? rowsPerPage)
                    : rowsPerPage
                }
                setRowsPerPage={n => {
                  setRowsPerPage(n);
                  onRowsPerPageChange?.(n);
                }}
                totalItems={totalItems}
                showImagePreview={showImagePreview}
                enableRowSelection={enableRowSelection}
                selectedIds={selectedRows}
                setSelectedIds={handleSelectionChange}
                paginationPosition={paginationPosition}
                actions={action}
                viewAction={handleViewCreative}
                deleteAction={handleDeleteCreative}
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(auto-fill, minmax(200px, 1fr))',
                  sm: 'repeat(auto-fill, minmax(250px, 1fr))',
                  md: 'repeat(auto-fill, minmax(280px, 1fr))',
                },
                gap: 2,
                p: 2,
              }}
            >
              {rows?.map((creative: any) => {
                const mediaFiles = getMediaFiles(creative);
                const hasMultipleMedia = mediaFiles.length > 1;
                const isSelected = selectedCreatives?.some(
                  (selected: any) => selected.id === creative.id
                );
                const normalizedType = normalizeCreativeType(creative.type);
                const isVast = normalizedType === CREATIVE_TYPE.VAST;

                // const isUrlCreative =
                //   !creative.files || Object.keys(creative.files).length === 0;
                const isUrlCreativeType = isUrlCreative(creative.type);

                const handleSelect = () => {
                  if (!enableRowSelection) {
                    // just preview if selection is disabled
                    return showImagePreview(creative);
                  }

                  if (isSelected) {
                    // remove from selection
                    setSelectedCreatives(prev =>
                      prev.filter(
                        (selected: any) => selected.id !== creative.id
                      )
                    );
                  } else {
                    // add to selection
                    setSelectedCreatives(prev => [...prev, creative]);
                  }
                };

                return (
                  <Box
                    key={`creative-${creative.id}`}
                    sx={{
                      border: isSelected
                        ? `2px solid ${theme.palette.primary.main}`
                        : `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? theme.palette.grey[800]
                          : theme.palette.background.paper,
                      boxShadow:
                        theme.palette.mode === 'dark'
                          ? '0 2px 4px rgba(0,0,0,0.3)'
                          : '0 2px 4px rgba(0,0,0,0.1)',
                      position: 'relative',
                      '&:hover': {
                        boxShadow:
                          theme.palette.mode === 'dark'
                            ? '0 4px 8px rgba(0,0,0,0.5)'
                            : '0 4px 8px rgba(0,0,0,0.15)',
                        transform: 'translateY(-2px)',
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? theme.palette.grey[700]
                            : theme.palette.action.hover,
                      },
                    }}
                    onClick={handleSelect}
                    onMouseEnter={() => handleMouseEnter(creative.id)}
                    onMouseLeave={() => handleMouseLeave(creative.id)}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: 150,
                        bgcolor:
                          theme.palette.mode === 'dark'
                            ? theme.palette.grey[900]
                            : theme.palette.grey[50],
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                    >
                      {/* Carousel Container with Slide Animation */}
                      {/* Preview Area */}
                      {isUrlCreativeType ? (
                        <img
                          src={isVast ? VAST_THUMBNAIL : WEBVIEW_THUMBNAIL}
                          alt={creative.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      ) : mediaFiles.length > 1 ? (
                        (() => {
                          const currentIndex =
                            currentMediaIndex[creative.id] || 0;
                          const translateX =
                            currentIndex * (100 / mediaFiles.length);

                          return (
                            <Box
                              sx={{
                                display: 'flex',
                                width: `${mediaFiles.length * 100}%`,
                                height: '100%',
                                transform: `translateX(-${translateX}%)`,
                                transition:
                                  'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                willChange: 'transform',
                              }}
                            >
                              {mediaFiles.map(
                                (media: any, mediaIndex: number) => (
                                  <Box
                                    key={`${creative.id}-${mediaIndex}`}
                                    sx={{
                                      width: `${100 / mediaFiles.length}%`,
                                      height: '100%',
                                      flexShrink: 0,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    {media.src
                                      ?.toLowerCase()
                                      .endsWith('.mp4') ? (
                                      <video
                                        src={media.src}
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover',
                                        }}
                                      />
                                    ) : (
                                      <img
                                        src={media.src}
                                        alt={creative.name}
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover',
                                        }}
                                      />
                                    )}
                                  </Box>
                                )
                              )}
                            </Box>
                          );
                        })()
                      ) : mediaFiles.length === 1 && mediaFiles[0].src ? (
                        mediaFiles[0].src.toLowerCase().endsWith('.mp4') ? (
                          <video
                            src={mediaFiles[0].src}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        ) : (
                          <img
                            src={mediaFiles[0].src}
                            alt={creative.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        )
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'text.secondary',
                            height: '100%',
                          }}
                        >
                          <CloudUploadIcon sx={{ fontSize: 40, mb: 1 }} />
                          <Typography variant="caption">No preview</Typography>
                        </Box>
                      )}

                      {/* Duration Overlay - Bottom Right */}
                      {!isUrlCreativeType && creative.duration && (
                        <Chip
                          label={creative.duration}
                          size="small"
                          sx={{
                            position: 'absolute',
                            bottom: 8,
                            right: 8,
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? 'rgba(0, 0, 0, 0.8)'
                                : 'rgba(0, 0, 0, 0.7)',
                            color: theme.palette.common.white,
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      )}

                      {/* Manual carousel controls removed - only auto-advance */}

                      {/* Media Counter - Only show if multiple media */}
                      {hasMultipleMedia && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? 'rgba(0, 0, 0, 0.8)'
                                : 'rgba(0, 0, 0, 0.7)',
                            color: theme.palette.common.white,
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                          }}
                        >
                          {(currentMediaIndex[creative.id] || 0) + 1} /{' '}
                          {mediaFiles.length}
                        </Box>
                      )}
                    </Box>

                    <Box
                      sx={{
                        p: 1,
                        backgroundColor: 'transparent',
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        noWrap
                        color="text.primary"
                      >
                        {creative.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {isUrlCreativeType
                          ? creative.name
                          : (() => {
                              const currentMedia = getCurrentMedia(creative);
                              const resolution =
                                currentMedia?.resolution?.width &&
                                currentMedia?.resolution?.height
                                  ? `${currentMedia.resolution.width}x${currentMedia.resolution.height}`
                                  : creative.resolution;

                              const parts = [
                                resolution,
                                creative.duration,
                              ].filter(Boolean);
                              return parts.join(' • ');
                            })()}
                      </Typography>

                      {creative.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                          noWrap
                        >
                          {creative.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {showButton && (
        <Fab
          color="primary"
          aria-label="add"
          onClick={() => dispatch(setAddCreativeModal({ open: true }))}
          sx={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            boxShadow: '0px 3px 5px -1px rgba(0,0,0,0.2)',
          }}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
};

export default Creative;
